/**
 * useSiteAlarms — React Query hook for the alarm list for a site.
 *
 * Query key shape is ['alarms', siteId, ...] which matches the invalidation
 * pattern used by useAlarmSubscription ('alarm:transition' → invalidateQueries
 * on ['alarms', siteId]). Do NOT rename the key prefix or WS invalidation
 * will silently stop triggering refetches.
 *
 * TWO SOURCES, ONE LIST (#434, #628)
 *
 * The History bucket reads a different table: cleared rows are purged from
 * active_alarms ~60s after clearing, so history is served from the immutable
 * alarm_history table (30-day window) via listHistoryForSite instead of
 * listForSite. (Not a hypertable, despite what migration 029 declared — the
 * create_hypertable call never ran on cloud_platform; see issue #647.)
 *
 * Source selection is now driven by the full set of selected buckets rather
 * than by a single collapsed scalar. Previously the caller narrowed the
 * multi-select array to one value (or `undefined` for 2+) and this hook
 * branched on `status === 'history'`; selecting History alongside any other
 * bucket therefore fell through to the active source and dropped every
 * historical row without any error (#628). The status array is now the input,
 * so "both buckets" fetches both sources and merges.
 */

import { useQuery } from '@tanstack/react-query';
import { alarmsApi, type ListFilters } from '../services/alarmsApi';
import {
  mergeAlarmSources,
  serverStatusFilter,
  sourcesForStatuses,
} from '../components/alarms/merge-alarm-sources';
import type { AlarmsListResponse } from '../types/alarm.types';

export interface SiteAlarmsInput {
  /** Selected status buckets. Empty means "no bucket filter" → active only. */
  statuses: readonly string[];
  /** Single-severity server-side pre-filter; undefined when 0 or 2+ selected. */
  severity?: string;
}

const EMPTY_SUMMARY: AlarmsListResponse['summary'] = {
  critical: 0,
  warning: 0,
  awaiting_ack: 0,
  stale: 0,
};

export function useSiteAlarms(siteId: string, input: SiteAlarmsInput) {
  const { statuses, severity } = input;
  const { active, history } = sourcesForStatuses(statuses);
  const status = serverStatusFilter(statuses);

  // Serialized into the key so a bucket change refetches. `statuses` is a new
  // array identity on every render, so spread the values rather than the array.
  const keyStatuses = [...statuses].sort().join(',');

  return useQuery<AlarmsListResponse>({
    queryKey: ['alarms', siteId, 'list', { statuses: keyStatuses, severity }],
    queryFn: async () => {
      // Server-side status narrowing is only sent when a single bucket is
      // selected. For a blended selection the server filter is omitted and the
      // page's matchesFilter does the narrowing — widening the payload is safe,
      // choosing the wrong table was not.
      const activeFilters: ListFilters = { status, severity };
      const historyFilters: ListFilters = { severity };

      const [activeRes, historyRes] = await Promise.all([
        active ? alarmsApi.listForSite(siteId, activeFilters) : null,
        history ? alarmsApi.listHistoryForSite(siteId, historyFilters) : null,
      ]);

      const alarms = mergeAlarmSources(
        activeRes?.alarms ?? [],
        historyRes?.alarms ?? [],
      );

      return {
        alarms,
        total: alarms.length,
        // Operator-actionable counts (awaiting_ack / stale) come from the
        // active source only — history rows are cleared by definition, so
        // folding them in would inflate the "needs action" figure.
        summary: activeRes?.summary ?? historyRes?.summary ?? EMPTY_SUMMARY,
      };
    },
    staleTime: 10_000,
    enabled: !!siteId,
  });
}
